import { useState } from "react";
import { Link } from "react-router-dom";

import { refusalText } from "../../../../api/errors";
import { useApiCommand, useApiQuery } from "../../../../api/hooks";
import type { Drawn, ElementRow, Id, LineRow, SymbolSet, SymbolSetItem } from "../../../../api/types";
import { Button, EmptyState, Loading, ShapeDrawing, SheetIcon } from "../../../../ui";
import { useDocumentKinds, useRefresh } from "../../hooks";
import { lineIsGone, lineNeverValidated } from "../../lib/lineRules";
import { elementPath } from "../../lib/paths";
import { ElementPicker } from "../ElementPicker";
import { Check, FormError, Select, TextInput } from "../fields";
import { ElementMarks, LineItem } from "../marks";
import { kindWord } from "./words";

/** The read of the symbol set, as the page holds it: a pick says "loading" or the error rather than "missing". */
export interface SymbolsRead {
  data: SymbolSet | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
}

/** One pick for each kind B drawing an item or component kind is shown on, and the roles of its connection points. */
export function PickFor({ elementId, symbols, editable }: { elementId: Id; symbols: SymbolsRead; editable: boolean }) {
  const view = useApiQuery("element", { id: elementId });
  const kinds = useDocumentKinds();
  const row = view.data?.element;
  const isItem = row?.kind === "item";
  const page = useApiQuery("item_page", { id: elementId }, { enabled: isItem });

  if (view.isPending) return <Loading>Reading the element</Loading>;
  if (view.isError) return <EmptyState title="It could not be opened">{refusalText(view.error)}</EmptyState>;
  if (!row) return null;
  if (row.kind !== "item" && row.kind !== "component_kind") {
    return <EmptyState title="Only items and component kinds are drawn">Choose an item or a component kind.</EmptyState>;
  }
  const isConnection = row.kind === "item" && row.content.is_connection === true;
  const what: "symbol" | "line_style" = isConnection ? "line_style" : "symbol";
  const drawingCode = (id: Id) => kinds.kinds.find((k) => k.id === id)?.code ?? "";
  const roles = (page.data?.components ?? []).map((c) => ({ code: c.element.code, name: c.element.name }));

  const slots: Drawn[] =
    row.kind === "item"
      ? (page.data?.drawn ?? [])
      : kinds.drawings.filter((k) => k.deliverableKind === "B").map((k) => ({ document_kind_id: k.id, name: k.name, deliverable_kind: "B", not_drawn: false }));

  return (
    <div className="pick-for">
      <p>
        <Link to={elementPath(row)}>{row.name}</Link> <ElementMarks element={row} />
      </p>
      {isConnection && <p className="muted">A Connection that is drawn gets a line style instead of a symbol.</p>}
      {isItem && page.isPending ? (
        <Loading>Reading where it is drawn</Loading>
      ) : isItem && page.isError ? (
        <EmptyState title="Where it is drawn could not be read">{refusalText(page.error)}</EmptyState>
      ) : slots.length === 0 ? (
        <EmptyState title="It is not drawn on any kind of drawing yet">
          A symbol belongs to a kind B drawing the element is shown on. Add the &quot;is shown on&quot; link first: drawn on which kinds of drawing is asked when you add it, and an
          existing item can get the link from its page.
        </EmptyState>
      ) : (
        <ul className="slot-list">
          {slots.map((slot) => (
            <SlotRow
              key={slot.document_kind_id}
              elementId={elementId}
              element={row}
              lines={view.data?.lines ?? []}
              slot={slot}
              slotCode={drawingCode(slot.document_kind_id)}
              what={what}
              symbols={symbols}
              roles={roles}
              editable={editable}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** The value of the field that chooses a symbol, or null for the one plain symbol of a kind of drawing. */
function chosenValue(line: LineRow): string | null {
  const by = line.content.chosen_by;
  if (by && typeof by === "object" && !Array.isArray(by)) {
    const code = (by as Record<string, unknown>).value_code;
    return typeof code === "string" && code ? code : null;
  }
  return null;
}

function codeOf(line: LineRow): string {
  return String(line.content.symbol_code ?? line.content.style_code ?? line.code ?? "");
}

/** What to tell the person next to a symbol the SME validated: it is not swapped at once. */
function swapHint(old: LineRow): string {
  return old.removal
    ? "The symbol here is marked for removal. When the SME has validated the removal, pick the other one."
    : "The symbol here was validated by the SME, so it cannot be swapped at once. Mark it for removal above. When the SME has validated the removal, pick the other one.";
}

/** What to tell the person who pressed "Use another symbol" on such a symbol: nothing was sent. */
function swapRefusal(old: LineRow): string {
  return old.removal
    ? "The symbol here is marked for removal already. Nothing was sent: wait for the SME's decision, then pick the other one."
    : "The symbol here was validated by the SME. Nothing was sent: mark it for removal first.";
}

interface SlotProps {
  elementId: Id;
  element: ElementRow;
  lines: readonly LineRow[];
  slot: Drawn;
  slotCode: string;
  what: "symbol" | "line_style";
  symbols: SymbolsRead;
  roles: Array<{ code: string; name: string }>;
  editable: boolean;
}

function SlotRow({ elementId, element, lines, slot, slotCode, what, symbols, roles, editable }: SlotProps) {
  const [code, setCode] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [byValue, setByValue] = useState(false);
  const [fieldId, setFieldId] = useState("");
  const [valueCodes, setValueCodes] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const pick = useApiCommand("pick_symbol");
  const takeOut = useApiCommand("withdraw_line");
  const refresh = useRefresh();
  const isGeometric = slot.deliverable_kind === "C";
  const word = kindWord(what);
  // The lines that hold the symbols picked for this kind of drawing (one, or one for each value of a field).
  const slotLines = lines.filter((line) => line.kind === what && line.content.document_kind_id === slot.document_kind_id && !lineIsGone(line));
  const plainLine = slotLines.find((line) => chosenValue(line) === null);
  const plainWaits = !!plainLine && !lineNeverValidated(plainLine);
  // A component kind's slot is made here and carries no pick: the live line of the slot says what is picked.
  const liveLine = plainLine ?? slotLines[0];
  const current = slot.symbol ?? slot.line_style ?? (liveLine ? { code: codeOf(liveLine) } : undefined);

  const every = (symbols.data?.items ?? []).filter((item) => item.kind === what);
  const all = every.filter((item) => !item.withdrawn); // a withdrawn shape is never picked again
  const here = all.filter((item) => item.kind_of_drawing === slotCode);
  const options = showAll || here.length === 0 ? all : here;
  const byCode = (wanted: string): SymbolSetItem | undefined => every.find((item) => item.code === wanted);
  const field = useApiQuery("element", { id: fieldId }, { enabled: byValue && !!fieldId });
  const listId = typeof field.data?.element.content.pick_list_id === "string" ? field.data.element.content.pick_list_id : "";
  const list = useApiQuery("element", { id: listId }, { enabled: !!listId });
  const values = (list.data?.lines ?? []).filter((line) => line.kind === "pick_value" && line.state !== "withdrawn");

  /** The line a new pick for this case would replace: the server refuses a second symbol for the same kind of drawing and case. */
  const existingFor = (valueCode: string | null) => slotLines.find((line) => chosenValue(line) === valueCode);

  const send = async (symbolCode: string, valueCode?: string) => {
    // "Use another symbol" (CP-12) is one step for the person: the old one is taken out, then the new one is picked.
    const old = existingFor(valueCode ?? null);
    if (old) await takeOut.mutateAsync({ id: old.id, expected_version: old.version });
    await pick.mutateAsync({
      id: elementId,
      document_kind_id: slot.document_kind_id,
      kind: what,
      symbol_code: symbolCode,
      chosen_by: valueCode && fieldId ? { field_id: fieldId, value_code: valueCode } : null,
    });
  };

  /** The symbol one of these picks would replace, when it cannot be taken out: the SME validated it. */
  const blockedBy = (picks: Array<[string | null, string]>): LineRow | null => {
    for (const [value, wanted] of picks) {
      const old = existingFor(value);
      if (old && codeOf(old) !== wanted && !lineNeverValidated(old)) return old;
    }
    return null;
  };

  const submit = async () => {
    setProblem(null);
    let sentSomething = false;
    try {
      if (byValue) {
        const chosen = Object.entries(valueCodes).filter(([, wanted]) => wanted);
        if (!fieldId || chosen.length === 0) {
          setProblem(`Choose the field, and a ${word} for at least one of its values.`);
          return;
        }
        const blocked = blockedBy(chosen);
        if (blocked) {
          setProblem(swapRefusal(blocked));
          return;
        }
        for (const [value, wanted] of chosen) {
          const old = existingFor(value);
          if (old && codeOf(old) === wanted) continue;
          sentSomething = true;
          await send(wanted, value);
        }
      } else {
        if (!code) {
          setProblem(`Choose a ${word} of the library first.`);
          return;
        }
        if (plainLine && codeOf(plainLine) === code) {
          setProblem(`That ${word} is the one picked here already.`);
          return;
        }
        const blocked = blockedBy([[null, code]]);
        if (blocked) {
          setProblem(swapRefusal(blocked));
          return;
        }
        sentSomething = true;
        await send(code);
      }
      setCode("");
    } catch {
      // The refusal is shown from the command's own state.
    } finally {
      // Also after a refusal: the old symbol may be taken out already, and the page must read the lines as they are now.
      if (sentSomething) refresh("item_page", "item_snapshot", "element", "symbol_set", "sme_card");
    }
  };

  const currentItem = current ? byCode(current.code) : undefined;
  const optionList = options.map((item) => ({ value: item.code, label: `${item.code} (${item.title})` }));

  return (
    <li className="slot" data-not-drawn={slot.not_drawn || undefined}>
      <div className="slot__head">
        <SheetIcon kind={slot.deliverable_kind} label={slot.name} />
        <strong>{slot.name}</strong>
      </div>
      {slot.not_drawn ? (
        <p className="muted">Not drawn on this kind of drawing, so there is nothing to pick.</p>
      ) : isGeometric ? (
        <p className="muted">A geometric drawing (kind C). Symbols belong to kind B drawings, so nothing is picked here.</p>
      ) : (
        <>
          {current ? (
            <p className="slot__current">
              {currentItem && <ShapeDrawing shape={currentItem.shape} kind={what} label={`${what === "line_style" ? "Line style" : "Symbol"} ${current.code}`} size={40} />}
              Picked: <code>{current.code}</code>
              {symbols.isPending ? (
                <span className="muted"> (reading the symbol library)</span>
              ) : symbols.isError ? (
                <span className="muted"> (the symbol library could not be read)</span>
              ) : !currentItem ? (
                <span className="muted"> (in neither the library nor this discipline)</span>
              ) : currentItem.withdrawn ? (
                <span className="muted"> (withdrawn{currentItem.replaced_by ? `: ${currentItem.replaced_by} replaces it` : ""}; give it another {word})</span>
              ) : null}
            </p>
          ) : (
            <p className="muted">Nothing is picked yet.</p>
          )}
          {editable && slotLines.length > 0 && (
            // The line of each symbol, with its own steps (CP-12): take it out, mark it for removal, take the mark off, drop my change.
            <ul className="line-list slot__lines" aria-label={`The ${word} lines on ${slot.name}`}>
              {slotLines.map((line) => (
                <LineItem key={line.id} line={line} element={element} />
              ))}
            </ul>
          )}
          {what === "symbol" && <Binding elementId={elementId} slot={slot} lines={slotLines} byCode={byCode} roles={roles} editable={editable} />}
          {editable && (
            <>
              {symbols.isPending ? (
                <Loading>Reading the symbol library</Loading>
              ) : symbols.isError ? (
                <p className="muted">The symbol library could not be read: {refusalText(symbols.error)}</p>
              ) : all.length === 0 ? (
                <p className="muted">No {word} in the library yet. Upload a drawing below.</p>
              ) : (
                <>
                  <Check label="Choose it by the value of a field" hint={`One ${word} for each value. Two values may share one.`} checked={byValue} onChange={setByValue} />
                  {!byValue && (
                    <Select label={`${what === "line_style" ? "Line style" : "Symbol"} of the library`} value={code} placeholder="Choose one" options={optionList} onChange={setCode} />
                  )}
                  {byValue && (
                    <>
                      <ElementPicker label="The field whose value selects it" kinds={["data_field"]} value={fieldId} onChange={(id) => { setFieldId(id); setValueCodes({}); }} />
                      {fieldId && !listId && !field.isPending && <p className="muted">This field has no pick list, so it has no values to choose by.</p>}
                      {values.length > 0 && (
                        <ul className="value-symbols">
                          {values.map((line) => {
                            const value = String(line.content.code ?? "");
                            return (
                              <li key={line.id}>
                                <Select
                                  label={`When the value is ${String(line.content.title ?? value)}`}
                                  value={valueCodes[value] ?? ""}
                                  placeholder={`No ${word} for this value`}
                                  options={optionList}
                                  onChange={(wanted) => setValueCodes((now) => ({ ...now, [value]: wanted }))}
                                />
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </>
                  )}
                  {here.length > 0 && all.length > here.length && <Check label="Also show the ones for other kinds of drawing" checked={showAll} onChange={setShowAll} />}
                  {!byValue && plainWaits && plainLine && <p className="slot__hint muted">{swapHint(plainLine)}</p>}
                  <div className="row">
                    <Button variant="primary" busy={pick.isPending || takeOut.isPending} onClick={() => void submit()}>
                      Use {byValue ? `these ${word}s` : plainLine ? `another ${word}` : `this ${word}`}
                    </Button>
                  </div>
                  <FormError message={problem ?? (takeOut.error ? refusalText(takeOut.error) : pick.error ? refusalText(pick.error) : null)} />
                </>
              )}
            </>
          )}
        </>
      )}
    </li>
  );
}

/** The line that holds the one list of roles of a kind of drawing: the plain symbol, else the first chosen by a value. */
function holderOf(lines: readonly LineRow[]): LineRow | undefined {
  return [...lines].sort((a, b) => Number(chosenValue(a) !== null) - Number(chosenValue(b) !== null) || codeOf(a).localeCompare(codeOf(b)))[0];
}

/** The roles the server holds on the holder line (its Next version first). */
function savedRoles(holder: LineRow | undefined): Record<string, string> {
  const pending = holder?.pending?.content as Record<string, unknown> | undefined;
  return ((pending?.points ?? holder?.content.points) ?? {}) as Record<string, string>;
}

/**
 * The roles form of one kind of drawing, made again whenever the holder line or its saved roles change (another symbol
 * was picked, a value-chosen one became the holder, the server answered), so that it never shows roles the server
 * does not hold. When another symbol replaced one that had roles, those roles are offered as an unsaved suggestion.
 */
function Binding(props: { elementId: Id; slot: Drawn; lines: readonly LineRow[]; byCode: (code: string) => SymbolSetItem | undefined; roles: Array<{ code: string; name: string }>; editable: boolean }) {
  const holder = holderOf(props.lines);
  const saved = savedRoles(holder);
  const key = `${holder?.id ?? ""}:${JSON.stringify(saved)}`;
  // the last holder that had roles saved, kept in state (set again during the render when it changes)
  const [last, setLast] = useState<{ holderId: string | undefined; roles: Record<string, string> }>({ holderId: holder?.id, roles: saved });
  const hasRoles = Object.keys(saved).length > 0;
  if (holder && (hasRoles || last.holderId === holder.id) && (last.holderId !== holder.id || JSON.stringify(last.roles) !== JSON.stringify(saved))) {
    setLast({ holderId: holder.id, roles: saved });
  }
  const swapped = !!holder && last.holderId !== holder.id && !hasRoles && Object.keys(last.roles).length > 0;
  const suggestion = swapped ? last.roles : null;
  // kept mounted while no symbol is picked (between taking one out and picking another), so the roles are remembered
  if (!holder) return null;
  return <PointsBinding key={key} {...props} holder={holder} saved={saved} suggestion={suggestion} />;
}

/**
 * `symbol_points`: each connection point of the symbol bound to a component role of the item (the roles belong to the
 * item, not to the shape). One list serves every symbol of a set chosen by a value.
 */
function PointsBinding({ elementId, slot, lines, byCode, roles, editable, saved, suggestion }: {
  elementId: Id;
  slot: Drawn;
  lines: readonly LineRow[];
  byCode: (code: string) => SymbolSetItem | undefined;
  roles: Array<{ code: string; name: string }>;
  editable: boolean;
  holder: LineRow | undefined;
  saved: Record<string, string>;
  suggestion: Record<string, string> | null;
}) {
  const [binding, setBinding] = useState<Record<string, string>>(suggestion ?? saved);
  const bind = useApiCommand("set_symbol_points");
  const refresh = useRefresh();
  const names = [...new Set(lines.flatMap((line) => byCode(codeOf(line))?.point_names ?? []))].sort();
  const hints = new Map<string, string>();
  for (const line of lines) {
    const shape = byCode(codeOf(line))?.shape as { connection_points?: Record<string, { role_hint?: string }> } | undefined;
    for (const [name, point] of Object.entries(shape?.connection_points ?? {})) if (point.role_hint) hints.set(name, point.role_hint);
  }
  const listId = `roles-${elementId}-${slot.document_kind_id}`;
  if (names.length === 0 && Object.keys(saved).length === 0 && !suggestion) {
    return <p className="muted">The {lines.length > 1 ? "symbols have" : "symbol has"} no connection points yet: place them in the library above, then say which component each one is.</p>;
  }
  const save = () => {
    const points = Object.fromEntries(Object.entries(binding).filter(([, role]) => role.trim()).map(([name, role]) => [name, role.trim()]));
    bind.mutate({ id: elementId, document_kind_id: slot.document_kind_id, points }, { onSuccess: () => refresh("element", "item_page", "item_snapshot", "sme_card") });
  };
  return (
    <div className="points-binding" role="group" aria-label={`Which component each connection point is, on ${slot.name}`}>
      <h4>Which component each connection point is</h4>
      {suggestion && (
        <p className="muted" role="status">
          The roles below are the ones of the symbol picked before. They are not saved for this symbol yet: check them, then press Save the roles.
        </p>
      )}
      {!suggestion && Object.keys(saved).length === 0 && editable && <p className="muted">No roles are saved for this symbol yet.</p>}
      <datalist id={listId}>
        {roles.map((r) => (
          <option key={r.code} value={r.code}>
            {r.name}
          </option>
        ))}
      </datalist>
      {[...new Set([...names, ...Object.keys(saved), ...Object.keys(suggestion ?? {})])].map((name) => (
        <TextInput
          key={name}
          label={`Point ${name}`}
          hint={hints.get(name) ? `The shape suggests ${hints.get(name)}.` : names.includes(name) ? undefined : "The symbol has no point of this name."}
          value={binding[name] ?? ""}
          list={listId}
          disabled={!editable}
          onChange={(role) => setBinding((now) => ({ ...now, [name]: role }))}
        />
      ))}
      {editable && (
        <div className="row">
          <Button variant="secondary" busy={bind.isPending} onClick={save}>
            Save the roles
          </Button>
        </div>
      )}
      <FormError message={bind.error ? refusalText(bind.error) : null} />
    </div>
  );
}
